<script>
/* global axios */
import { mapActions, mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default{
    name: "ChunkListItem",
    props: {
        id: {
            type: String
        },
        true_word_count: {
            type: Number
        },
        hit_count: {
            type: Number
        },
        content: {
            type: String
        },
        enabled: {
            type: Boolean
        },
        position: {
            type: Number
        },
        keywords: {
            type: Array
        },
        document_id: {
            type: String
        },
        isChunkExpanded: {
            type: Boolean
        },
        image_url: {
            type: String,
            default: null
        },
        engine: {
            type: String,
            default: 'dify'
        }
    },
    data() {
        return {
            localEnabled: this.enabled,
            isToggling: false,
            imageObjectUrl: null,
            imageLoadFailed: false,
            // Bumped on every loadImage() call (including on unmount) so an
            // in-flight request can tell, once its response lands, whether
            // it's still the one that should apply — image_url can change
            // (or the component can unmount) before the previous fetch
            // resolves, and axios/URL.createObjectURL give no cancellation.
            imageRequestId: 0,
        };
    },
    computed: {
        ...mapGetters({ accountId: 'getCurrentAccountId' }),
        isLightrag() {
            return this.engine === 'lightrag';
        },
        // A lightrag-generated image description chunk (as opposed to a plain
        // text chunk) — surfaced via a small visual tag, per the chunk id
        // convention the knowledge API uses for these rows.
        isImageDescription() {
            return typeof this.id === 'string' && this.id.includes('-mm-drawing-');
        }
    },
    watch: {
        enabled(newVal) {
            this.localEnabled = newVal;
        },
        image_url: {
            immediate: true,
            handler() {
                this.loadImage();
            }
        }
    },
    beforeUnmount() {
        // Invalidate any in-flight loadImage() request so its response
        // can't set imageObjectUrl (and thus leak an unreclaimed object
        // URL with nothing left around to revoke it) after this component
        // is gone.
        this.imageRequestId += 1;
        this.revokeImageObjectUrl();
    },
    methods: {
        ...mapActions('dify', ['enableChunk', 'disableChunk']),
        revokeImageObjectUrl() {
            if (this.imageObjectUrl) {
                URL.revokeObjectURL(this.imageObjectUrl);
                this.imageObjectUrl = null;
            }
        },
        // image_url is a short-lived (~10 min) signed path behind the Rails
        // knowledge proxy, which requires Devise Token Auth headers — a plain
        // <img src> can't carry those, so fetch it via axios (which does) and
        // render the result as a local object URL instead.
        // Backend fix (2026-08-17): the proxy no longer corrupts binary bytes,
        // so this goes back through it instead of calling the knowledge API
        // directly — re-add account_id as a query param since the signed URL
        // doesn't already carry it.
        async loadImage() {
            this.imageRequestId += 1;
            const requestId = this.imageRequestId;
            this.revokeImageObjectUrl();
            this.imageLoadFailed = false;
            if (!this.image_url) return;
            try {
                const separator = this.image_url.includes('?') ? '&' : '?';
                const { data } = await axios.get(
                    `/api/v1/accounts/${this.accountId}/proxy/knowledge${this.image_url}${separator}account_id=${this.accountId}`,
                    { responseType: 'blob' }
                );
                const objectUrl = URL.createObjectURL(data);
                // A newer loadImage() call (a new image_url, or this
                // component unmounting) has since superseded this request —
                // release the object URL we just created instead of either
                // leaking it or clobbering a newer image with a stale one.
                if (requestId !== this.imageRequestId) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }
                this.imageObjectUrl = objectUrl;
            } catch (err) {
                if (requestId !== this.imageRequestId) return;
                this.imageLoadFailed = true;
                console.error('Error loading chunk image:', err);
            }
        },
        async toggleChunk() {
            if (this.isToggling) return;
            this.isToggling = true;
            try {
                if (this.localEnabled) {
                    await this.disableChunk({ datasetId: this.$route.params.id, documentId: this.document_id, ids: [this.id] });
                } else {
                    await this.enableChunk({ datasetId: this.$route.params.id, documentId: this.document_id, ids: [this.id] });
                }
                this.localEnabled = !this.localEnabled;
                useAlert('Chunk toggled');
            } catch (err) {
                console.error(err);
            } finally {
                this.isToggling = false;
            }
        },
        handleClick(){
          // lightrag chunks aren't editable, but they're still viewable —
          // the click just opens the modal in read-only mode (see
          // ViewDocument.vue's :read-only prop on EditChunkModal).
          if (!this.isLightrag && !this.localEnabled){
            useAlert('Unable to edit disabled chunk');
            return;
          }
          this.$emit('clicked');
        }
    }
}
</script>

<template>
  <div
    @click="handleClick"
    class="bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl p-4 flex flex-col gap-3 shadow-sm transition-colors duration-200 relative group flex-1 w-full cursor-pointer"
    data-testid="chunk_list_item"
    :data-chunk-id="id"
  >
    <!-- Header: Chunk details -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center text-sm text-slate-600 dark:text-slate-300 gap-1 sm:gap-0">
      <p v-if="isLightrag" class="text-xs">
        {{
          position < 10
            ? $t('KNOWLEDGE.CHUNK_LIST_ITEM.CHUNK_INFO_LIGHTRAG_PADDED', { position: position.toString(), word_count: true_word_count })
            : $t('KNOWLEDGE.CHUNK_LIST_ITEM.CHUNK_INFO_LIGHTRAG', { position: position.toString(), word_count: true_word_count })
        }}
      </p>
      <p v-else class="text-xs">
        {{
          position < 10
            ? $t('KNOWLEDGE.CHUNK_LIST_ITEM.CHUNK_INFO_PADDED', { position: position.toString(), word_count: true_word_count, hit_count })
            : $t('KNOWLEDGE.CHUNK_LIST_ITEM.CHUNK_INFO', { position: position.toString(), word_count: true_word_count, hit_count })
        }}
      </p>
      <p v-if="isImageDescription" class="flex items-center gap-1 text-xs font-medium" data-testid="chunk_image_description_tag">
        🖼 {{ $t('KNOWLEDGE.CHUNK_LIST_ITEM.IMAGE_DESCRIPTION_TAG') }}
      </p>
      <p v-else-if="!isLightrag" class="flex items-center gap-2">
        <span class="font-medium" data-testid="chunk_status_label">{{ localEnabled ? $t('KNOWLEDGE.CHUNK_LIST_ITEM.ENABLED') : $t('KNOWLEDGE.CHUNK_LIST_ITEM.DISABLED') }}</span>
        <span
          class="w-3 h-3 rounded-full shadow-md"
          :class="{'bg-green-500': localEnabled, 'bg-slate-500 dark:bg-slate-400': !localEnabled}"
        ></span>
      </p>
    </div>

    <!-- Image (lightrag chunks with a rendered diagram/photo) -->
    <div v-if="image_url" class="max-w-full sm:max-w-[500px]">
      <img
        v-if="imageObjectUrl"
        :src="imageObjectUrl"
        loading="lazy"
        class="max-h-64 rounded-md border border-slate-200 dark:border-slate-600"
        data-testid="chunk_image"
      />
      <p v-else-if="imageLoadFailed" class="text-xs text-red-500" data-testid="chunk_image_error">
        {{ $t('KNOWLEDGE.CHUNK_LIST_ITEM.IMAGE_LOAD_ERROR') }}
      </p>
    </div>

    <!-- Content -->
    <div class="text-sm text-slate-800 dark:text-slate-100">
      <p data-testid="chunk_content" :class="{
        'line-clamp-2 max-w-full sm:max-w-[500px]': !isChunkExpanded,
        'whitespace-pre-wrap break-words max-w-full sm:max-w-[500px]': isChunkExpanded
      }">
        {{ content }}
      </p>
      <p class="text-xs text-slate-600 dark:text-slate-400 max-w-full sm:max-w-[500px] flex flex-wrap gap-2">
        <span v-for="keyword in keywords" :key="keyword">#{{ keyword }}</span>
      </p>
    </div>

    <!-- Action -->
    <div v-if="!isLightrag" class="absolute top-2 right-2 rounded-md bg-white dark:bg-slate-700 p-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
      <woot-button :disabled="!localEnabled" size="tiny" icon="edit" data-testid="chunk_edit_button" @click.stop="$emit('clicked')"></woot-button>
      <woot-button :disabled="!localEnabled" size="tiny" color-scheme="alert" icon="delete" data-testid="chunk_delete_button" @click.stop="$emit('delete')"></woot-button>
      <label
        class="bg-slate-100 dark:bg-slate-600 p-2 w-[40px] h-[20px] rounded-full relative"
        :class="{ 'opacity-50 cursor-not-allowed': isToggling }"
        data-testid="chunk_toggle"
        @click.stop.prevent="toggleChunk"
      >
        <input type="checkbox" class="peer/availability hidden" :checked="localEnabled" :disabled="isToggling" />
        <div class="absolute top-0 left-0 transition-all h-[20px] w-[20px] duration-500 rounded-full bg-slate-300 dark:bg-slate-400 peer-checked/availability:bg-woot-500 peer-checked/availability:translate-x-[100%]"></div>
      </label>
    </div>
    <!-- Lightrag chunks aren't editable, but are still viewable in full -->
    <div
      v-else
      v-tooltip.top="$t('KNOWLEDGE.CHUNK_LIST_ITEM.VIEW_CHUNK_TOOLTIP')"
      class="absolute top-2 right-2 rounded-md bg-white dark:bg-slate-700 p-1.5 flex opacity-0 group-hover:opacity-100 transition-opacity duration-200"
      data-testid="chunk_view_only_icon"
    >
      <fluent-icon icon="eye-show" size="16" class="text-slate-500 dark:text-slate-300" />
    </div>
  </div>
</template>